import React, { useState, useEffect } from 'react';
import { 
  Container, Box, Typography, TextField, Button, Grid, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  Card, CardContent, Divider, Snackbar, Alert, Chip, Badge, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  useMediaQuery, useTheme
} from '@mui/material';
import { MenuInicial } from "./MenuInicial.jsx";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, PRODUCTOSLISTS, RUTAPRODUCTOSIMAGENES, VENTAALTA } from '../services/apiConfig.js';
import axios from 'axios';
import { 
  Add, Remove, Delete, Save, Clear, Receipt,
  AttachMoney, CreditCard, AccountBalance, QrCode2, Close, Print, LocalDrink
} from '@mui/icons-material';
import Cargando from './utils/Cargando.jsx';
import { formatCurrency } from '../utils/formatCurrency.js';

const FACTURACION_ESTADO = 'facturacionestado.php';
const FACTURACION_EMITIR = 'facturacionemitir.php';

const TIPOS_COMPROBANTE = [
  { id: 1, nombre: 'Factura A' },
  { id: 6, nombre: 'Factura B' },
  { id: 11, nombre: 'Factura C' },
];

const TIPOS_DOCUMENTO = [
  { id: '80', nombre: 'CUIT' },
  { id: '96', nombre: 'DNI' },
  { id: '99', nombre: 'Consumidor Final' },
];

function FacturaElectronica() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const sucursalid = userData?.sucursalid;
  const usuarioid = userData?.usuarioid;

  const [productos, setProductos] = useState([]);
  const [productosFiltrados, setProductosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carrito, setCarrito] = useState([]);
  
  // Datos de la venta
  const [cliente, setCliente] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [observaciones, setObservaciones] = useState('');
  const [descuento, setDescuento] = useState(0);
  
  // Control de diálogo
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [observacionProducto, setObservacionProducto] = useState('');
  
  // Notificaciones
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Búsqueda de productos
  const [busquedaProducto, setBusquedaProducto] = useState('');

  // Facturación electrónica
  const [facturacionEstado, setFacturacionEstado] = useState(null);
  const [facturaDialogOpen, setFacturaDialogOpen] = useState(false);
  const [emitiendoFactura, setEmitiendoFactura] = useState(false);
  const [datosFactura, setDatosFactura] = useState({
    tipo_documento: '99',
    numero_documento: '',
    nombre_cliente: '',
    tipo_comprobante: 6
  });
  const [comprobanteResult, setComprobanteResult] = useState(null);

  useEffect(() => {
    fetchProductos();
    fetchFacturacionEstado();
  }, []);

  const fetchFacturacionEstado = async () => {
    try {
      const response = await axios.get(API_BASE_URL + FACTURACION_ESTADO);
      if (response.data.success) {
        setFacturacionEstado(response.data.data);
        setDatosFactura(prev => ({
          ...prev,
          tipo_comprobante: response.data.data.tipo_comprobante_predeterminado || 6
        }));
      }
    } catch (error) {
      console.error('Error al obtener estado de facturación:', error);
      setFacturacionEstado({ habilitado: false });
    }
  };

  const fetchProductos = async () => {
    try {
      const response = await axios.get(API_BASE_URL + PRODUCTOSLISTS);
      const productosActivos = response.data.filter(p => p.activo === "1" || p.activo === 1);
      setProductos(productosActivos);
      setProductosFiltrados(productosActivos);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar productos', error);
      setLoading(false);
    }
  };

  const handleBusquedaProducto = (event, value) => {
    if (value) {
      abrirDialogoProducto(value);
      setBusquedaProducto('');
    }
  };

  const abrirDialogoProducto = (producto) => {
    setProductoSeleccionado(producto);
    setCantidad(1);
    setObservacionProducto('');
    setDialogOpen(true);
  };

  const cerrarDialogoProducto = () => {
    setDialogOpen(false);
    setProductoSeleccionado(null);
    setCantidad(1);
    setObservacionProducto('');
  };

  const agregarAlCarrito = () => {
    if (!productoSeleccionado || cantidad <= 0) return;

    const itemExistente = carrito.find(item => item.id === productoSeleccionado.id);
    
    if (itemExistente) {
      setCarrito(carrito.map(item =>
        item.id === productoSeleccionado.id
          ? { ...item, cantidad: item.cantidad + cantidad, observaciones: observacionProducto }
          : item
      ));
    } else {
      setCarrito([...carrito, {
        id: productoSeleccionado.id,
        nombre: productoSeleccionado.nombre,
        precio: parseFloat(productoSeleccionado.precio),
        cantidad: cantidad,
        observaciones: observacionProducto,
        imagen: productoSeleccionado.fotos
      }]);
    }

    setSnackbar({ open: true, message: 'Producto agregado', severity: 'success' });
    cerrarDialogoProducto();
  };

  const modificarCantidad = (id, cambio) => {
    setCarrito(carrito.map(item =>
      item.id === id
        ? { ...item, cantidad: Math.max(1, item.cantidad + cambio) }
        : item
    ));
  };

  const eliminarDelCarrito = (id) => {
    setCarrito(carrito.filter(item => item.id !== id));
  };

  const calcularSubtotal = () => {
    return carrito.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);
  };

  const calcularTotal = () => {
    return calcularSubtotal() - parseFloat(descuento || 0);
  };

  const abrirDialogoFactura = () => {
    setDatosFactura({
      ...datosFactura,
      nombre_cliente: cliente || 'Consumidor Final'
    });
    setComprobanteResult(null);
    setFacturaDialogOpen(true);
  };

  const cerrarDialogoFactura = () => {
    setFacturaDialogOpen(false);
    setComprobanteResult(null);
  };

  const emitirFactura = async () => {
    if (calcularTotal() < 0) {
      setSnackbar({ open: true, message: 'El descuento no puede ser mayor al subtotal', severity: 'error' });
      return;
    }

    try {
      setEmitiendoFactura(true);
      
      const ventaData = {
        fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
        sucursal_id: sucursalid,
        usuario_id: usuarioid,
        cliente: cliente || null,
        total: calcularTotal(),
        descuento: parseFloat(descuento || 0),
        metodo_pago: metodoPago,
        estado: 'completada',
        observaciones: observaciones || null,
        productos: carrito.map(item => ({
          producto_id: item.id,
          cantidad: item.cantidad,
          precio_unitario: item.precio,
          observaciones: item.observaciones || null
        }))
      };

      const responseVenta = await axios.post(`${API_BASE_URL + VENTAALTA}`, ventaData);
      
      if (!responseVenta.data.success) {
        setSnackbar({ open: true, message: 'Error al registrar la venta', severity: 'error' });
        setEmitiendoFactura(false);
        return;
      }

      const ventaId = responseVenta.data.venta_id || responseVenta.data.id;

      const facturaData = {
        venta_id: ventaId,
        tipo_comprobante: datosFactura.tipo_comprobante,
        tipo_documento: datosFactura.tipo_documento,
        numero_documento: datosFactura.numero_documento || '0',
        nombre_cliente: datosFactura.nombre_cliente,
        total: calcularTotal(),
        productos: carrito
      };

      const responseFactura = await axios.post(API_BASE_URL + FACTURACION_EMITIR, facturaData);
      
      if (responseFactura.data.success) {
        setComprobanteResult(responseFactura.data.data);
        setSnackbar({ 
          open: true, 
          message: `Venta registrada. Comprobante ${responseFactura.data.data.cae}`, 
          severity: 'success' 
        });
        
        if (facturacionEstado?.auto_imprimir) {
          setTimeout(() => imprimirComprobante(responseFactura.data.data), 500);
        }
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Venta registrada pero error en factura: ' + responseFactura.data.message, 
          severity: 'warning' 
        });
      }
    } catch (error) {
      console.error('Error al emitir factura:', error);
      setSnackbar({ open: true, message: 'Error al procesar la venta', severity: 'error' });
    } finally {
      setEmitiendoFactura(false);
    }
  };

  const imprimirComprobante = (comprobante) => {
    const tipoNombre = TIPOS_COMPROBANTE.find(t => t.id === comprobante.tipo_comprobante)?.nombre || 'Comprobante';
    const contenido = `
      =====================================
      ${facturacionEstado?.razon_social || 'Empresa'}
      CUIT: ${facturacionEstado?.cuit || '-'}
      ${facturacionEstado?.domicilio || ''}
      =====================================
      
      ${tipoNombre}
      Punto de Venta: ${String(comprobante.punto_venta).padStart(4, '0')}
      Comp. Nro: ${String(comprobante.numero_comprobante).padStart(8, '0')}
      
      Fecha: ${new Date().toLocaleDateString('es-AR')}
      =====================================
      
      Cliente: ${datosFactura.nombre_cliente}
      ${datosFactura.tipo_documento !== '99' ? `CUIT: ${datosFactura.numero_documento}` : ''}
      
      ------------------------------------
      Detalle de productos:
      ------------------------------------
      ${carrito.map(item => `${item.nombre}
      x${item.cantidad} ..... ${formatCurrency(item.precio * item.cantidad)}`).join('\n')}
      ------------------------------------
      SUBTOTAL: ${formatCurrency(calcularSubtotal())}
      ${descuento > 0 ? `DESCUENTO: -${formatCurrency(descuento)}` : ''}
      TOTAL: ${formatCurrency(calcularTotal())}
      =====================================
      
      CAE: ${comprobante.cae}
      Vto CAE: ${comprobante.fecha_vencimiento}
      =====================================
    `;
    
    const printWindow = window.open('', '', 'width=300,height=600');
    printWindow.document.write('<pre style="font-family: monospace; font-size: 12px;">' + contenido + '</pre>');
    printWindow.document.close();
    printWindow.print();
  };

  const finalizarVentaConFactura = () => {
    cerrarDialogoFactura();
    setTimeout(() => {
      navigate('/ventas');
    }, 2000);
  };

  const finalizarVenta = async () => {
    if (carrito.length === 0) {
      setSnackbar({ open: true, message: 'Agregue productos a la venta', severity: 'warning' });
      return;
    }

    if (calcularTotal() < 0) {
      setSnackbar({ open: true, message: 'El descuento no puede ser mayor al subtotal', severity: 'error' });
      return;
    }

    if (facturacionEstado?.habilitado && facturacionEstado?.mostrar_preview) {
      abrirDialogoFactura();
      return;
    }

    if (facturacionEstado?.habilitado) {
      abrirDialogoFactura();
      return;
    }
    if (carrito.length === 0) {
      setSnackbar({ open: true, message: 'Agregue productos a la venta', severity: 'warning' });
      return;
    }

    if (calcularTotal() < 0) {
      setSnackbar({ open: true, message: 'El descuento no puede ser mayor al subtotal', severity: 'error' });
      return;
    }

    const ventaData = {
      fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
      sucursal_id: sucursalid,
      usuario_id: usuarioid,
      cliente: cliente || null,
      total: calcularTotal(),
      descuento: parseFloat(descuento || 0),
      metodo_pago: metodoPago,
      estado: 'completada',
      observaciones: observaciones || null,
      productos: carrito.map(item => ({
        producto_id: item.id,
        cantidad: item.cantidad,
        precio_unitario: item.precio,
        observaciones: item.observaciones || null
      }))
    };

    try {
      const response = await axios.post(`${API_BASE_URL + VENTAALTA}`, ventaData);
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Venta registrada exitosamente', severity: 'success' });
        setTimeout(() => {
          navigate('/ventas');
        }, 1500);
      } else {
        setSnackbar({ open: true, message: 'Error al registrar la venta', severity: 'error' });
      }
    } catch (error) {
      console.error('Error al finalizar venta', error);
      setSnackbar({ open: true, message: 'Error al registrar la venta', severity: 'error' });
    }
  };

  const limpiarVenta = () => {
    setCarrito([]);
    setCliente('');
    setMetodoPago('efectivo');
    setObservaciones('');
    setDescuento(0);
  };

  // Métodos de pago con iconos
  const metodosPago = [
    { value: 'efectivo', label: 'Efectivo', icon: <AttachMoney />, color: '#2e7d32' },
    { value: 'tarjeta_debito', label: 'Débito', icon: <CreditCard />, color: '#1976d2' },
    { value: 'tarjeta_credito', label: 'Crédito', icon: <CreditCard />, color: '#ed6c02' },
    { value: 'transferencia', label: 'Transfer.', icon: <AccountBalance />, color: '#9c27b0' },
    { value: 'mercadopago', label: 'M.Pago', icon: <QrCode2 />, color: '#0095ff' }
  ];

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <Cargando />
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      
      <Box sx={{ my: 3 }}>
        {/* Header con resumen rápido */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Punto de Venta
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selecciona productos y completa la venta
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Badge badgeContent={carrito.length} color="primary">
              <Chip 
                label={`Productos: ${carrito.reduce((sum, item) => sum + item.cantidad, 0)}`}
                color="primary"
                variant="outlined"
              />
            </Badge>
            <Chip 
              label={`Total: ${formatCurrency(calcularTotal())}`}
              color="success"
              sx={{ fontSize: '1.1rem', fontWeight: 'bold', px: 2 }}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Panel izquierdo - Búsqueda de productos */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 220px)' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Productos Disponibles
              </Typography>
              
              <Autocomplete
                options={productosFiltrados}
                getOptionLabel={(option) => `${option.nombre} - ${formatCurrency(option.precio)}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <img 
                      src={`${RUTAPRODUCTOSIMAGENES}/${option.fotos}`}
                      style={{ width: 40, height: 40, marginRight: 10, objectFit: 'cover', borderRadius: 4 }}
                      alt={option.nombre}
                    />
                    <Box>
                      <Typography variant="body1">{option.nombre}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(option.precio)} - {option.rubro}
                      </Typography>
                    </Box>
                  </Box>
                )}
                onChange={handleBusquedaProducto}
                value={busquedaProducto}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Buscar producto..." 
                    variant="outlined"
                    fullWidth
                    autoFocus
                  />
                )}
              />

              <Box sx={{ mt: 2, maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                <Grid container spacing={2}>
                  {productosFiltrados.slice(0, 50).map((producto) => (
                    <Grid item xs={6} sm={4} lg={3} key={producto.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { 
                            boxShadow: 6,
                            transform: 'translateY(-4px)',
                            transition: 'all 0.2s'
                          },
                          height: '100%'
                        }}
                        onClick={() => abrirDialogoProducto(producto)}
                      >
                        <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={`${RUTAPRODUCTOSIMAGENES}/${producto.fotos}`}
                              alt={producto.nombre}
                              style={{ 
                                width: '100%', 
                                height: '100px', 
                                objectFit: 'cover', 
                                borderRadius: 8 
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mt: 1, 
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              minHeight: '2.5em',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {producto.nombre}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color="primary"
                            sx={{ fontWeight: 'bold', mt: 0.5 }}
                          >
                            {formatCurrency(producto.precio)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Panel derecho - Detalle de venta */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Detalle de Venta
              </Typography>
              
              {/* Lista de productos */}
              <TableContainer sx={{ flexGrow: 1, mb: 2, maxHeight: '35vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cant.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {carrito.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary" variant="body2">
                            No hay productos agregados
                          </Typography>
                          <Typography color="text.secondary" variant="caption">
                            Selecciona productos del catálogo
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      carrito.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.nombre}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => modificarCantidad(item.id, -1)}
                                sx={{ bgcolor: 'grey.100' }}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <Typography sx={{ minWidth: 25, textAlign: 'center', fontWeight: 'bold' }}>
                                {item.cantidad}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => modificarCantidad(item.id, 1)}
                                sx={{ bgcolor: 'grey.100' }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(item.precio)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(item.precio * item.cantidad)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => eliminarDelCarrito(item.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              {/* Información del cliente y pago */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Cliente (opcional)"
                  fullWidth
                  size="small"
                  value={cliente}
                  onChange={(e) => setCliente(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Métodos de Pago como Botones */}
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Método de Pago:
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {metodosPago.map((metodo) => (
                    <Grid item xs={6} sm={4} key={metodo.value}>
                      <Button
                        fullWidth
                        variant={metodoPago === metodo.value ? 'contained' : 'outlined'}
                        onClick={() => setMetodoPago(metodo.value)}
                        startIcon={metodo.icon}
                        sx={{
                          py: 1.5,
                          fontSize: '0.85rem',
                          backgroundColor: metodoPago === metodo.value ? metodo.color : 'transparent',
                          borderColor: metodo.color,
                          color: metodoPago === metodo.value ? 'white' : metodo.color,
                          '&:hover': {
                            backgroundColor: metodoPago === metodo.value ? metodo.color : `${metodo.color}20`,
                            borderColor: metodo.color,
                          }
                        }}
                      >
                        {metodo.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      label="Descuento"
                      type="number"
                      fullWidth
                      size="small"
                      value={descuento}
                      onChange={(e) => setDescuento(e.target.value)}
                      InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Observaciones"
                      fullWidth
                      size="small"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Resumen de totales */}
              <Box sx={{ mb: 2, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(calcularSubtotal())}
                  </Typography>
                </Box>
                {descuento > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="error">Descuento:</Typography>
                    <Typography variant="body1" color="error" sx={{ fontWeight: 500 }}>
                      -{formatCurrency(descuento)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>TOTAL:</Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(calcularTotal())}
                  </Typography>
                </Box>
              </Box>

              {/* Botones de acción */}
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    startIcon={<Save />}
                    onClick={finalizarVenta}
                    disabled={carrito.length === 0}
                    sx={{ 
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}
                  >
                    Cobrar
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    size="large"
                    startIcon={<Clear />}
                    onClick={limpiarVenta}
                    sx={{ py: 1.5 }}
                  >
                    Limpiar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Diálogo para agregar producto */}
      <Dialog open={dialogOpen} onClose={cerrarDialogoProducto} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Agregar Producto
          <IconButton onClick={cerrarDialogoProducto} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {productoSeleccionado && (
            <Box sx={{ minWidth: 300 }}>
              <Box sx={{ textAlign: 'center', mb: 2, bgcolor: 'grey.100', borderRadius: 2, p: 2 }}>
                <img
                  src={`${RUTAPRODUCTOSIMAGENES}/${productoSeleccionado.fotos}`}
                  alt={productoSeleccionado.nombre}
                  style={{ width: '100%', maxHeight: 250, objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {productoSeleccionado.nombre}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {productoSeleccionado.descripcion}
              </Typography>
              <Chip 
                label={productoSeleccionado.rubro} 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                {formatCurrency(productoSeleccionado.precio)}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
                <Typography variant="body1" sx={{ minWidth: 80 }}>Cantidad:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton 
                    onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                    sx={{ bgcolor: 'grey.200' }}
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    type="number"
                    size="small"
                    value={cantidad}
                    onChange={(e) => setCantidad(Math.max(1, parseInt(e.target.value) || 1))}
                    InputProps={{ inputProps: { min: 1 } }}
                    sx={{ width: 80, textAlign: 'center' }}
                  />
                  <IconButton 
                    onClick={() => setCantidad(cantidad + 1)}
                    sx={{ bgcolor: 'grey.200' }}
                  >
                    <Add />
                  </IconButton>
                </Box>
              </Box>

              <TextField
                label="Observaciones (opcional)"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={observacionProducto}
                onChange={(e) => setObservacionProducto(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                  Subtotal: {formatCurrency(productoSeleccionado.precio * cantidad)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cerrarDialogoProducto} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={agregarAlCarrito} variant="contained" color="primary" size="large">
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Facturación Electrónica */}
      <Dialog open={facturaDialogOpen} onClose={cerrarDialogoFactura} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Receipt sx={{ mr: 1, color: 'primary.main' }} />
            Comprobante Electrónico
          </Box>
          <IconButton onClick={cerrarDialogoFactura} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {comprobanteResult ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                ¡Comprobante Autorizado!
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>CAE:</strong> {comprobanteResult.cae}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Vto CAE:</strong> {comprobanteResult.fecha_vencimiento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Comprobante:</strong> {String(comprobanteResult.punto_venta).padStart(4, '0')}-{String(comprobanteResult.numero_comprobante).padStart(8, '0')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Complete los datos del comprobante para emitir la factura electrónica
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo Comprobante</InputLabel>
                    <Select
                      value={datosFactura.tipo_comprobante}
                      label="Tipo Comprobante"
                      onChange={(e) => setDatosFactura({ ...datosFactura, tipo_comprobante: e.target.value })}
                    >
                      {TIPOS_COMPROBANTE.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>{tipo.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo Documento</InputLabel>
                    <Select
                      value={datosFactura.tipo_documento}
                      label="Tipo Documento"
                      onChange={(e) => setDatosFactura({ ...datosFactura, tipo_documento: e.target.value })}
                    >
                      {TIPOS_DOCUMENTO.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>{tipo.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {datosFactura.tipo_documento !== '99' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Número Documento"
                      fullWidth
                      size="small"
                      value={datosFactura.numero_documento}
                      onChange={(e) => setDatosFactura({ ...datosFactura, numero_documento: e.target.value })}
                      placeholder="20345678901"
                      inputProps={{ maxLength: 11 }}
                    />
                  </Grid>
                )}
                
                <Grid item xs={12} sm={datosFactura.tipo_documento === '99' ? 12 : 6}>
                  <TextField
                    label="Nombre / Razón Social"
                    fullWidth
                    size="small"
                    value={datosFactura.nombre_cliente}
                    onChange={(e) => setDatosFactura({ ...datosFactura, nombre_cliente: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1">{formatCurrency(calcularSubtotal())}</Typography>
                </Box>
                {descuento > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" color="error">Descuento:</Typography>
                    <Typography variant="body1" color="error">-{formatCurrency(descuento)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">TOTAL:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(calcularTotal())}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {comprobanteResult ? (
            <>
              <Button 
                startIcon={<Print />} 
                onClick={() => imprimirComprobante(comprobanteResult)}
                variant="outlined"
              >
                Imprimir
              </Button>
              <Button 
                variant="contained" 
                onClick={finalizarVentaConFactura}
              >
                Finalizar
              </Button>
            </>
          ) : (
            <>
              <Button onClick={cerrarDialogoFactura} variant="outlined" disabled={emitiendoFactura}>
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={emitirFactura}
                disabled={emitiendoFactura}
                startIcon={emitiendoFactura ? <CircularProgress size={20} /> : <Receipt />}
              >
                {emitiendoFactura ? 'Emitiendo...' : 'Emitir Comprobante'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default FacturaElectronica;
